import React from "react";

/**
 * CameraOptions component - Displays camera configuration options panel
 * 
 * Features:
 * - Provides flash mode settings (off, on, auto)
 * - Offers timer options for delayed capture (0s, 3s, 10s)
 * - Allows aspect ratio selection (1:1, 3:4, 9:16)
 * - Shows active settings with visual highlighting
 * - Compact dropdown-style interface
 * - Click outside to close functionality
 * 
 * Props:
 * @param {string} flashMode - Current flash mode setting ("off", "on", or "auto")
 * @param {Function} setFlashMode - Function to update flash mode
 * @param {number} timer - Current timer setting in seconds (0, 3, or 10)
 * @param {Function} setTimer - Function to update timer setting
 * @param {string} ratio - Current aspect ratio setting ("1:1", "3:4", or "9:16")
 * @param {Function} setRatio - Function to update aspect ratio setting
 */
export default function CameraOptions({
  flashMode, // Current flash mode: "off" | "on" | "auto"
  setFlashMode, // Function to update flash mode setting
  timer, // Current timer duration: 0 | 3 | 10 seconds
  setTimer, // Function to update timer setting
  ratio, // Current aspect ratio: "1:1" | "3:4" | "9:16"
  setRatio, // Function to update aspect ratio setting
}) {
  return (
    <>
      {/* 
        Camera options container
        - Styled with CSS class .camera-options
        - Provides dropdown-style interface for camera settings
      */}
      <div className="camera-options">
        {/* 
          Flash settings section
          - Allows user to control camera flash behavior
          - Three modes: off (never use), on (always use), auto (automatic)
        */}
        <div className="option-row">
          <span>Flash</span>
          <div className="option-buttons">
            {/* 
              Map through flash mode options
              - Highlights currently selected mode with 'active' class
              - Updates flash mode on button click
            */}
            {["off", "on", "auto"].map((mode) => (
              <button
                key={mode} // Unique key for React rendering
                className={flashMode === mode ? "active" : ""} // Highlight active option
                onClick={() => setFlashMode(mode)} // Update flash mode on click
              >
                {mode} {/* Display mode name */}
              </button>
            ))}
          </div>
        </div>

        {/* 
          Timer settings section
          - Provides countdown timer options for delayed photo capture
          - Options: Off (instant), 3 seconds, 10 seconds
        */}
        <div className="option-row">
          <span>Timer</span>
          <div className="option-buttons">
            {/* 
              Map through timer options
              - Shows "Off" for 0 seconds, otherwise displays seconds with "s" suffix
              - Highlights currently selected timer setting
            */}
            {[0, 3, 10].map((t) => (
              <button
                key={t} // Unique key for React rendering
                className={timer === t ? "active" : ""} // Highlight active option
                onClick={() => setTimer(t)} // Update timer setting on click
              >
                {t === 0 ? "Off" : `${t}s`} {/* Display "Off" or seconds */}
              </button>
            ))}
          </div>
        </div>

        {/* 
          Aspect ratio settings section
          - Controls the shape/proportion of captured photos
          - Options: Square (1:1), Portrait (3:4), Tall Portrait (9:16)
        */}
        <div className="option-row">
          <span>Ratio</span>
          <div className="option-buttons">
            {/* 
              Map through aspect ratio options
              - Each ratio represents width:height proportions
              - Highlights currently selected ratio setting
            */}
            {["1:1", "3:4", "9:16"].map((r) => (
              <button
                key={r} // Unique key for React rendering
                className={ratio === r ? "active" : ""} // Highlight active option
                onClick={() => setRatio(r)} // Update ratio setting on click
              >
                {r} {/* Display ratio string */}
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
